import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { types as sdkTypes } from '../../util/sdkLoader';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser, userDisplayNameAsString } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, NamedLink, ResponsiveImage, TimestampBadge } from '../../components';

import css from './NewListingCard.module.css';
import ReactPlayer from 'react-player/lazy';
import { BiHeart, BiSolidHeart } from 'react-icons/bi';
import { FiTrash } from 'react-icons/fi';
import { FaPlay } from 'react-icons/fa';
import { connect, useSelector } from 'react-redux';
import axios from 'axios';
import { apiBaseUrl } from '../../util/api';
import { addToLikeList } from '../../ducks/user.duck';
import { setListingLikedState } from '../../containers/SearchPage/SearchPage.duck';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import trashBin from '../../assets/svg/trash_bin.svg';
import addToBag from '../../assets/svg/add_to_bag.svg';
import { MAX_CART_ITEM } from '../../config/configDefault';
import { userLikeCount } from '../../ducks/user.duck';
import { useDispatch } from 'react-redux';
import { getMediaPlaybackInfo } from '../../util/media';
import { trackAddToWishlist } from '../../util/metaPixel';
import { trackAddToWishlist as trackGA4AddToWishlist } from '../../util/googleAnalytics';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (!price || typeof price !== 'object' || typeof price.amount !== 'number' || !price.currency) {
    return {};
  }
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

export const PriceMaybe = props => {
  const { price, publicData, config, intl, className } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);

  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={classNames(css.price, className)} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

const titleCaseFormatter = (str = '') =>
  str
    ?.trim()
    ?.split(' ')
    ?.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    ?.join(' ') || '';

const getSizeDisplay = (publicData, config) => {
  if (!publicData) return null;
  const sizeKey = Object.keys(publicData).find(key => /^.*(size).*$/i.test(key));
  const rawVal =
    (sizeKey ? publicData[sizeKey] : null) ||
    publicData.size ||
    publicData.sizeName ||
    publicData.itemSize ||
    publicData.clothingSize ||
    publicData.shoeSize ||
    publicData.selectedSize ||
    publicData.Women_Men_Accessories_size ||
    publicData.Mens_Shoes_Sizes ||
    publicData.Womens_Shoes_Sizes;

  if (!rawVal) return null;

  const configLabel = sizeKey && config?.customListingField?.[sizeKey]?.[rawVal]?.label;
  const resolved = configLabel || (typeof rawVal === 'object' ? rawVal?.label || rawVal?.value : rawVal);
  if (!resolved) return null;
  const str = String(resolved).trim();
  if (!str) return null;
  return str;
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @param {boolean?} props.showDelete
 * @param {boolean?} props.showProfileUrl
 * @param {boolean?} props.showListingName
 * @param {boolean?} props.showAddToCart
 * @param {Function?} props.onAddList
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const NewListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const {
    history,
    currentUser,
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    likeSuccess,
    likePending,
    likeError,
    onAddList,
    isAuthenticated,
    showListingName = true,
    showRating = true,
    showDelete = false,
    showProfileUrl = false,
    showAddToCart = false,
    isLiked: isPropsLiked,
    handleCart,
    isAddedInCart,
    totalItemsInCart,
    onAddOneUserCartCount,
    onFetchListing,
    currentSort,
    showTimeBadge = true,
  } = props;
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const reduxLikesCount = useSelector(state => state.SearchPage?.listingLikesCounts?.[id]);
  const reduxIsLiked = useSelector(state =>
    state.SearchPage?.favroiteListingIds ? state.SearchPage.favroiteListingIds.has(id) : undefined
  );
  const resolvedIsLiked =
    typeof isPropsLiked === 'boolean'
      ? isPropsLiked
      : typeof reduxIsLiked === 'boolean'
      ? reduxIsLiked
      : showDelete
      ? true
      : false;
  const rawLikes =
    currentListing?.attributes?.metadata?.likesCount ??
    currentListing?.attributes?.publicData?.likesCount ??
    0;
  const serverLikesCount = typeof rawLikes === 'number' ? rawLikes : Number(rawLikes) || 0;
  const isCurrentlyLiked = resolvedIsLiked;
  const effectiveLikesCount =
    !isCurrentlyLiked && serverLikesCount === 0
      ? 0
      : typeof reduxLikesCount === 'number'
      ? reduxLikesCount
      : isCurrentlyLiked && serverLikesCount === 0
      ? 1
      : serverLikesCount;

  // const history = useHistory();
  const [hover, setHover] = useState(false);
  const [liked, setLiked] = useState(resolvedIsLiked);
  const [likesCount, setLikesCount] = useState(effectiveLikesCount);
  const [isInCart, setIsInCart] = useState(isAddedInCart ?? false);
  const [videoError, setVideoError] = useState(false);
  const [useFallbackPlayback, setUseFallbackPlayback] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const playerRef = useRef(null);
  const videoContainerRef = useRef(null);
  const classes = classNames(rootClassName || css.root, className);
  const { title = '', price, publicData, state } = currentListing.attributes;
  const { comparePrice } = publicData;
  const { brand, qrListingTitle = ' ' } = publicData || {};
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = userDisplayNameAsString(author, '');
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const routes = useRouteConfiguration();
  const { Money } = sdkTypes;
  // const enableMulticart = currentUser?.attributes?.profile?.publicData?.multicart;
  const enableMulticart = true;
  const dispatch = useDispatch();

  const cartPage = history.location.pathname === '/my-bag' ? true : false;
  const searchPage = history.location.pathname === '/s' ? true : false;
  const homePage = history.location.pathname === '/' ? true : false;
  const wishlistPage = history.location.pathname === '/wishlist' ? true : false;
  const hasStock = currentListing?.currentStock?.attributes?.quantity > 0;

  const { variantPrefix = 'listing-card' } = config.layout.listingImage;
  const aspectWidth = 3;
  const aspectHeight = 4;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
      onMouseEnter: () => setActiveListing(currentListing.id),
      onMouseLeave: () => setActiveListing(null),
    }
    : null;
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const media = getMediaPlaybackInfo(listing?.attributes?.publicData);
  const videoUrl =
    useFallbackPlayback && media?.fallbackPlaybackUrl
      ? media.fallbackPlaybackUrl
      : media?.playbackUrl;

  const hoverVid = videoUrl || null;

  const thumbnailUrl = media?.thumbnailUrl;

  // if (videoUrl && typeof videoUrl === 'string') {
  //   const srcUpdated = process.env.REACT_APP_CDN_ROOT_DOMAIN
  //     ? `${process.env.REACT_APP_CDN_ROOT_DOMAIN}/${videoUrl.split('/').pop()}`
  //     : videoUrl;
  //   const ext = srcUpdated
  //     .split('.')
  //     .pop()
  //     .toLowerCase();
  //   if (videoExtensions.includes(ext)) {
  //     hoverVid = srcUpdated;
  //   }
  // }
  const handleProgress = ({ playedSeconds }) => {
    if (playedSeconds >= 3 && playerRef.current) {
      playerRef.current.seekTo(0);
    }
  };

  const handleVideoError = () => {
    if (
      !useFallbackPlayback &&
      media?.fallbackPlaybackUrl &&
      media.fallbackPlaybackUrl !== media?.playbackUrl
    ) {
      setUseFallbackPlayback(true);
      setVideoError(false);
      setVideoReady(false);
      return;
    }

    setVideoError(true);
  };

  const handleVideoReady = () => {
    setVideoReady(true);
  };

  useEffect(() => {
    if (!hover) {
      if (playerRef.current) {
        playerRef.current.seekTo(0);
      }
    }
  }, [hover]);

  // Reset videoReady when video URL changes (different listing)
  useEffect(() => {
    setVideoReady(false);
    setVideoError(false);
  }, [hoverVid]);

  useEffect(() => {
    setUseFallbackPlayback(false);
  }, [media?.playbackUrl, media?.fallbackPlaybackUrl]);

  useEffect(() => {
    if (!isMobile) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHover(true);
        } else {
          setHover(false);
        }
      },
      {
        threshold: 0.2,
      }
    );

    const target = videoContainerRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [isMobile]);

  useEffect(() => {
    setIsInCart(isAddedInCart ?? false);
  }, [isAddedInCart]);

  const targetLikeRef = useRef(resolvedIsLiked);
  const serverLikedStateRef = useRef(resolvedIsLiked);
  const isSyncingRef = useRef(false);
  const isMountedRef = useRef(true);
  const selfSyncedValueRef = useRef(resolvedIsLiked);

  useEffect(() => {
    setLikesCount(effectiveLikesCount);
  }, [id, effectiveLikesCount]);

  useEffect(() => {
    const nextLiked = resolvedIsLiked;
    if (nextLiked === selfSyncedValueRef.current) {
      // This update just echoes the like/dislike we triggered ourselves; the
      // local optimistic state is already correct, so skip.
      return;
    }
    selfSyncedValueRef.current = nextLiked;
    setLiked(nextLiked);
    targetLikeRef.current = nextLiked;
    serverLikedStateRef.current = nextLiked;
  }, [resolvedIsLiked]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const syncLikeWithServer = async latestCount => {
    if (isSyncingRef.current) return;
    if (targetLikeRef.current === serverLikedStateRef.current) return;

    isSyncingRef.current = true;
    try {
      while (targetLikeRef.current !== serverLikedStateRef.current) {
        let response = null;
        if (onAddList) {
          response = await onAddList(id, author.id.uuid);
        } else {
          response = await axios.post(
            `${apiBaseUrl()}/api/likes/add`,
            {
              listingId: id,
              authorId: author?.id?.uuid,
            },
            {
              withCredentials: true,
            }
          );
        }
        if (response?.data?.message === 'Like added successfully') {
          serverLikedStateRef.current = true;
        } else if (response?.data?.message === 'Like removed successfully') {
          serverLikedStateRef.current = false;
        } else {
          serverLikedStateRef.current = targetLikeRef.current;
        }
        // Keep the cross-page favorites set in sync so other mounted/cached
        // pages reflect this like/dislike without waiting for a re-fetch.
        selfSyncedValueRef.current = serverLikedStateRef.current;
        dispatch(setListingLikedState(id, serverLikedStateRef.current, latestCount));
        userLikeCount(dispatch);
        onFetchListing &&
          currentSort &&
          onFetchListing({ sort: currentSort, page: 1, append: false, config });
      }
    } catch (error) {
      console.error('Failed to sync like status with server:', error);
      targetLikeRef.current = serverLikedStateRef.current;
      if (isMountedRef.current) {
        setLiked(serverLikedStateRef.current);
        setLikesCount(effectiveLikesCount);
      }
      selfSyncedValueRef.current = serverLikedStateRef.current;
      dispatch(setListingLikedState(id, serverLikedStateRef.current, effectiveLikesCount));
    } finally {
      isSyncingRef.current = false;
      if (isMountedRef.current) setLiked(targetLikeRef.current);
    }
  };

  const isCurrentUsersListing = author?.id?.uuid === currentUser?.id?.uuid;
  const handleListLike = () => {
    if (isCurrentUsersListing) return;
    if (!isAuthenticated) {
      history.push(createResourceLocatorString('LoginPage', routes, {}, {}));
      return;
    }

    const nextState = !targetLikeRef.current;
    targetLikeRef.current = nextState;

    const nextCount = Math.max(0, likesCount + (nextState ? 1 : -1));

    // 1. Instant local UI feedback
    setLiked(nextState);
    setLikesCount(nextCount);
    if (nextState) {
      trackAddToWishlist(currentListing);
      trackGA4AddToWishlist(currentListing, currentUser?.id?.uuid);
    }

    // 2. Sync with server immediately (no debounce) so the count/heart never
    // lags behind or gets dropped by a quick navigation away from the card.
    syncLikeWithServer(nextCount);
  };

  const handleRemoveFromWishlist = e => {
    e.stopPropagation();
    e.preventDefault();
    if (isCurrentUsersListing) return;
    if (!isAuthenticated) {
      history.push(createResourceLocatorString('LoginPage', routes, {}, {}));
      return;
    }
    if (!targetLikeRef.current && !liked) return;

    const nextState = false;
    targetLikeRef.current = nextState;
    const nextCount = Math.max(0, likesCount - 1);

    setLiked(nextState);
    setLikesCount(nextCount);

    syncLikeWithServer(nextCount);
  };

  const displayLikesCount = liked ? Math.max(1, likesCount) : likesCount;

  const formattedProfileUrl = titleCaseFormatter(
    author.attributes.profile.publicData?.rawProfileUrl ||
      author.attributes.profile.publicData?.profileUrl
  );
  // author.attributes.profile.publicData?.profileUrl
  //   ?.trim()
  //   ?.split(' ')
  //   ?.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
  //   ?.join(' ') || '';

  const sizeVal = getSizeDisplay(publicData, config);

  return (
    <NamedLink className={classes} name="ListingPageBySlug" params={{ slug: qrListingTitle }}>
      <div ref={videoContainerRef}>
        <AspectRatioWrapper
          className={classNames(css.aspectRatioWrapper, css.newImageWrapper)}
          width={aspectWidth}
          height={aspectHeight}
          onMouseEnter={!isMobile ? () => setHover(true) : undefined}
          onMouseLeave={!isMobile ? () => setHover(false) : undefined}
          {...setActivePropsMaybe}
        >
          {showTimeBadge && (
            <TimestampBadge listing={currentListing} isSold={!hasStock || state === 'closed'} />
          )}
          {hoverVid && (
            <div className={css.playBadge}>
              <FaPlay className={css.playIcon} />
            </div>
          )}
          {hoverVid ? (
            <>
              <ReactPlayer
                key={`${hoverVid}-${useFallbackPlayback ? 'fallback' : 'primary'}`}
                className={css.reactPlayer}
                ref={playerRef}
                url={hoverVid}
                controls={false}
                playing={hover}
                loop={true}
                onProgress={handleProgress}
                onError={handleVideoError}
                onReady={handleVideoReady}
                width={'100%'}
                muted
                playsInline
                style={{
                  opacity: hover && videoReady && !videoError ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out',
                  position: hover && videoReady && !videoError ? 'relative' : 'absolute',
                }}
                config={{
                  file: {
                    attributes: {
                      playsInline: true,
                      'webkit-playsinline': 'true',
                      preload: 'metadata',
                      crossOrigin: 'anonymous',
                    },
                  },
                }}
              />
              {thumbnailUrl ? (
                <img
                  src={thumbnailUrl}
                  alt={title}
                  className={css.rootForImage}
                  style={{
                    opacity: hover && videoReady && !videoError ? 0 : 1,
                    transition: 'opacity 0.3s ease-in-out',
                    position: hover && videoReady && !videoError ? 'absolute' : 'relative',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <ResponsiveImage
                  rootClassName={css.rootForImage}
                  alt={title}
                  image={firstImage}
                  variants={variants}
                  sizes={renderSizes}
                  style={{
                    opacity: hover && videoReady && !videoError ? 0 : 1,
                    transition: 'opacity 0.3s ease-in-out',
                    position: hover && videoReady && !videoError ? 'absolute' : 'relative',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              )}
            </>
          ) : (
            <LazyImage
              rootClassName={css.rootForImage}
              alt={title}
              image={firstImage}
              variants={variants}
              sizes={renderSizes}
            />
          )}

          {showDelete ? (
            <button
              className={classNames(css.floatingBtnRoot, css.deleteFloat)}
              onClick={handleRemoveFromWishlist}
              aria-label="Remove from wishlist"
            >
              <img src={trashBin} alt="delete icon" />
            </button>
          ) : (
            /* Bottom-left heart pill badge with total likes count */
            <button
              type="button"
              className={classNames(css.heartPillBtn, { [css.heartPillBtnLiked]: liked })}
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleListLike();
              }}
              aria-label={liked ? 'Remove from favorites' : 'Add to favorites'}
            >
              <svg
                className={classNames(css.heartPillIcon, { [css.heartPillIconLiked]: liked })}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill={liked ? '#fbb03b' : 'none'}
                stroke="#fbb03b"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              {displayLikesCount > 0 ? (
                <span className={css.heartPillCount}>{displayLikesCount}</span>
              ) : null}
            </button>
          )}
        </AspectRatioWrapper>
      </div>
      <div className={css.info}>
        <div className={css.infoLeft}>
          <PriceMaybe
            className={css.priceLabel}
            price={price}
            publicData={publicData}
            config={config}
            intl={intl}
          />
          <div className={css.brandLabel}>
            {config.customBrandRef?.[brand]?.label?.toUpperCase() ?? (brand?.toUpperCase() || '')}
          </div>
          {sizeVal ? <div className={css.sizeLabel}>{sizeVal}</div> : null}
        </div>
      </div>
    </NamedLink>
  );
};

const mapStateToProps = state => {
  const { currentUser, likeSuccess, likePending, likeError } = state.user;
  const { isAuthenticated } = state.auth;

  return {
    currentUser,
    isAuthenticated,
    likeSuccess: likeSuccess,
    likePending: likePending,
    likeError: likeError,
  };
};

// const mapDispatchToProps = dispatch => ({
//   onAddList: (listingId, authorId) => dispatch(addToLikeList(listingId, authorId)),
// });

const areEqual = (prevProps, nextProps) => {
  return (
    prevProps.listing?.id?.uuid === nextProps.listing?.id?.uuid &&
    prevProps.isLiked === nextProps.isLiked &&
    prevProps.likeSuccess === nextProps.likeSuccess &&
    prevProps.likePending === nextProps.likePending
  );
};

export default connect(mapStateToProps)(React.memo(NewListingCard, areEqual));
